// src/components/grammar/GrammarExercise.jsx - WORKING Drag & Drop
// src/components/grammar/GrammarExercise.jsx - BLACK & WHITE DESIGN
import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, ArrowRight, RotateCcw, Bookmark, Lightbulb, Shuffle, Trash2, X } from 'lucide-react';
import AnimatedButton from '../ui/AnimatedButton';
import { useTranslation } from 'react-i18next';

// Normalize a full sentence: collapse whitespace, strip trailing punctuation,
// lowercase only the first character (sentence-start), preserves German noun capitalization.
const normalizeSentence = (text) => {
  const s = (text || '').trim().replace(/\s+/g, ' ').replace(/[.!?]+$/, '').trim();
  return s.length > 0 ? s.charAt(0).toLowerCase() + s.slice(1) : s;
};

// Normalize a word/phrase fill-in: trim and strip trailing punctuation, but keep case
// so that German noun capitalization is still checked.
const normalizeWord = (text) =>
  (text || '').trim().replace(/[.!?]+$/, '').trim();

// Support pipe-separated alternatives ("ans1 | ans2 | ans3")
const checkSentence = (userAnswer, correctAnswer) => {
  const normalized = normalizeSentence(userAnswer);
  return String(correctAnswer)
    .split('|')
    .map(normalizeSentence)
    .some(alt => alt === normalized);
};

const checkWord = (userAnswer, correctAnswer) => {
  const normalized = normalizeWord(userAnswer);
  return String(correctAnswer)
    .split('|')
    .map(normalizeWord)
    .some(alt => alt === normalized);
};

const GrammarExercise = ({ exercise, onComplete, onNext, isBookmarked, onToggleBookmark }) => {
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [fillInAnswers, setFillInAnswers] = useState({});
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [startTime] = useState(Date.now());
  const [correctedText, setCorrectedText] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const { t } = useTranslation();

  // Drag and drop state
  const [wordBank, setWordBank] = useState([]);
  const [sentenceArea, setSentenceArea] = useState([]);
  const [leftColumnItems, setLeftColumnItems] = useState([]);
  const [rightColumnItems, setRightColumnItems] = useState([]);
  const [selectedPairItem, setSelectedPairItem] = useState(null);
  const [matchedPairs, setMatchedPairs] = useState([]);
  const [incorrectPair, setIncorrectPair] = useState(null);
  const [processedExerciseId, setProcessedExerciseId] = useState(null);

  // Hover states for answers
  const [hoveredAnswer, setHoveredAnswer] = useState(null);

  // Hover states for action buttons (wave effect)
  const [isCheckHovered, setIsCheckHovered] = useState(false);
  const [isRetryHovered, setIsRetryHovered] = useState(false);
  const [isNextHovered, setIsNextHovered] = useState(false);

  // Touch drag state
  const [touchDragData, setTouchDragData] = useState(null);

  // Initialize exercises
  useEffect(() => {
    if (exercise && exercise.id !== processedExerciseId) {
      setShowFeedback(false);

      if (exercise.exerciseType === 'sentenceBuilding' && exercise.stimulus) {
        const shuffledWords = [...exercise.stimulus].sort(() => Math.random() - 0.5);
        setWordBank(shuffledWords.map((word, index) => ({
          id: `word-${index}-${Date.now()}`,
          text: word
        })));
        setSentenceArea([]);
      }

      if (exercise.exerciseType === 'matchingPairs' && exercise.stimulus) {
        const leftItems = [];
        const rightItems = [];

        exercise.correctAnswer.forEach((pair, index) => {
          leftItems.push({
            id: `left-${index}`,
            text: pair.left,
            pairText: pair.right,
            column: 'left'
          });
          rightItems.push({
            id: `right-${index}`,
            text: pair.right,
            pairText: pair.left,
            column: 'right'
          });
        });

        setLeftColumnItems(leftItems);
        setRightColumnItems(rightItems.sort(() => Math.random() - 0.5));
        setSelectedPairItem(null);
        setMatchedPairs([]);
        setIncorrectPair(null);
      }

      setProcessedExerciseId(exercise.id);
    }
  }, [exercise, processedExerciseId]);

  const handleSubmit = () => {
    let correct = false;
    let userAnswer = '';

    switch (exercise.exerciseType) {
      case 'multipleChoice':
        correct = selectedAnswer === exercise.correctAnswer;
        userAnswer = selectedAnswer;
        break;
      case 'matchingPairs':
        const totalItems = leftColumnItems.length + rightColumnItems.length;
        correct = matchedPairs.length === totalItems && totalItems > 0;
        userAnswer = 'all pairs matched';
        break;
      case 'errorCorrection':
        const originalIncorrect = exercise.stimulus;
        const expectedCorrect = exercise.correctStimulus;
        const userCorrection = originalIncorrect.replace(exercise.incorrectPart, correctedText);
        correct = checkSentence(userCorrection, expectedCorrect);
        userAnswer = userCorrection;
        break;
      case 'fillInTheBlanks':
        const allCorrect = exercise.answers.every((answer, index) =>
          checkWord(fillInAnswers[`blank_${index}`] || '', answer.correct)
        );
        correct = allCorrect;
        userAnswer = JSON.stringify(fillInAnswers);
        break;
      case 'sentenceTransformation':
        correct = checkSentence(selectedAnswer, exercise.correctAnswer);
        userAnswer = selectedAnswer;
        break;
      case 'sentenceBuilding':
        const builtSentence = sentenceArea.map(item => item.text).join(' ');
        correct = checkSentence(builtSentence, exercise.correctAnswer);
        userAnswer = builtSentence;
        break;
      default:
        correct = true;
        userAnswer = 'completed';
    }

    setIsCorrect(correct);
    setShowFeedback(true);

    const timeSpent = Math.round((Date.now() - startTime) / 1000);

    onComplete({
      exerciseId: exercise.id,
      correct,
      timeSpent,
      selectedAnswer: userAnswer
    });
  };

  const handleNext = () => {
    setSelectedAnswer('');
    setFillInAnswers({});
    setShowFeedback(false);
    setIsCorrect(false);
    setWordBank([]);
    setSentenceArea([]);
    onNext();
  };

  const handleReset = () => {
    setSelectedAnswer('');
    setFillInAnswers({});
    setShowFeedback(false);
    setIsCorrect(false);

    if (exercise.exerciseType === 'matchingPairs') {
      setRightColumnItems(prev => [...prev].sort(() => Math.random() - 0.5));
      setSelectedPairItem(null);
      setMatchedPairs([]);
      setIncorrectPair(null);
    }

    if (exercise.exerciseType === 'sentenceBuilding') {
      const shuffledWords = [...exercise.stimulus].sort(() => Math.random() - 0.5);
      setWordBank(shuffledWords.map((word, index) => ({
        id: `word-${index}-${Date.now()}`,
        text: word
      })));
      setSentenceArea([]);
    }
  };

  const handlePairSelection = (item) => {
    if (showFeedback || matchedPairs.includes(item.id)) {
      return;
    }

    if (!selectedPairItem) {
      setSelectedPairItem(item);
      setIncorrectPair(null);
      return;
    }

    if (selectedPairItem.id === item.id) {
      setSelectedPairItem(null);
      return;
    }

    if (selectedPairItem.pairText === item.text && selectedPairItem.column !== item.column) {
      setMatchedPairs(prev => [...prev, selectedPairItem.id, item.id]);
      setSelectedPairItem(null);
    } else {
      setIncorrectPair({ item1: selectedPairItem.text, item2: item.text });
      setSelectedPairItem(null);
      setTimeout(() => setIncorrectPair(null), 800);
    }
  };

  const updateFillInAnswer = (blank, value) => {
    setFillInAnswers(prev => ({
      ...prev,
      [blank]: value
    }));
  };

  // Drag and drop handlers (keeping existing logic)
  const handleDragStart = (e, word, sourceType) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({
      word: word,
      sourceType: sourceType
    }));
    e.dataTransfer.effectAllowed = 'move';
    e.target.style.opacity = '0.5';
    e.target.style.transform = 'scale(0.95)';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    e.target.style.transform = 'scale(1)';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDropOnSentence = (e, dropIndex = null) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      const dragData = JSON.parse(e.dataTransfer.getData('text/plain'));
      const { word, sourceType } = dragData;

      if (sourceType === 'sentence') {
        setSentenceArea(prev => {
          const currentIndex = prev.findIndex(w => w.id === word.id);
          if (dropIndex === null) return prev;

          const newArray = prev.filter(w => w.id !== word.id);
          let adjustedIndex = dropIndex;
          if (currentIndex < dropIndex) {
            adjustedIndex = dropIndex - 1;
          }
          newArray.splice(adjustedIndex, 0, word);
          return newArray;
        });
        return;
      }

      if (sourceType === 'bank') {
        const newWord = { ...word, id: `sentence-${Date.now()}-${Math.random()}` };
        setSentenceArea(prev => {
          const newArray = [...prev];
          if (dropIndex !== null && dropIndex <= newArray.length) {
            newArray.splice(dropIndex, 0, newWord);
          } else {
            newArray.push(newWord);
          }
          return newArray;
        });
        setWordBank(prev => prev.filter(w => w.id !== word.id));
      }
    } catch (error) {
      console.error('Error handling drop:', error);
    }
  };

  const handleDropOnBank = (e) => {
    e.preventDefault();
    try {
      const dragData = JSON.parse(e.dataTransfer.getData('text/plain'));
      const { word, sourceType } = dragData;

      if (sourceType === 'sentence') {
        const newWord = { ...word, id: `bank-${Date.now()}-${Math.random()}` };
        setWordBank(prev => [...prev, newWord]);
        setSentenceArea(prev => prev.filter(w => w.id !== word.id));
      }
    } catch (error) {
      console.error('Error handling drop on bank:', error);
    }
  };

  const shuffleWordBank = () => {
    if (showFeedback) return;
    setWordBank(prev => [...prev].sort(() => Math.random() - 0.5));
  };

  const clearSentence = () => {
    if (showFeedback) return;
    const wordsToReturn = sentenceArea.map(word => ({
      ...word,
      id: `bank-${Date.now()}-${Math.random()}`
    }));
    setWordBank(prev => [...prev, ...wordsToReturn]);
    setSentenceArea([]);
  };

  const handleWordBankClick = (word) => {
    if (showFeedback) return;
    const newWord = { ...word, id: `sentence-${Date.now()}-${Math.random()}` };
    setSentenceArea(prev => [...prev, newWord]);
    setWordBank(prev => prev.filter(w => w.id !== word.id));
  };

  const handleSentenceClick = (word) => {
    if (showFeedback) return;
    const newWord = { ...word, id: `bank-${Date.now()}-${Math.random()}` };
    setWordBank(prev => [...prev, newWord]);
    setSentenceArea(prev => prev.filter(w => w.id !== word.id));
  };

  // RENDER METHODS with BLACK & WHITE DESIGN
const renderMultipleChoice = () => {
  const renderStimulusWithGap = (text) => {
    const parts = text.split(/(__+)|(\[---\])/g);
    return parts.map((part, index) => {
      if (part && (part.match(/^__+$/) || part === '[---]')) {
        return (
          <span
            key={index}
            className="inline-block mx-2 px-4 py-2 min-w-[100px] border-2 border-dashed border-black bg-gray-50 rounded-lg"
          >
            &nbsp;
          </span>
        );
      }
      return part || null;
    });
  };

  return (
    <div className="space-y-4">
      {/* Question and Stimulus */}
      <div className="bg-white p-4 sm:p-6 rounded-xl border-2 border-black relative overflow-hidden">
        <div className="absolute top-2 left-2 w-2 h-2 border-t-2 border-l-2 border-black/20"></div>
        <div className="absolute bottom-2 right-2 w-2 h-2 border-b-2 border-r-2 border-black/20"></div>

        <p className="text-base sm:text-lg font-space font-bold text-black mb-3">
          {exercise.question}
        </p>
        <div className="text-gray-700 text-base sm:text-lg leading-relaxed">
          {renderStimulusWithGap(exercise.stimulus)}
        </div>
      </div>

      {/* Answer Options with SMOOTH Drawing Animation */}
      <div className="space-y-3">
        {exercise.options.map((option, index) => {
  const isSelected = selectedAnswer === option;
  const isThisCorrect = option === exercise.correctAnswer;
  const isThisWrong = showFeedback && isSelected && !isThisCorrect;
  const isHovered = hoveredAnswer === index;

  return (
    <div
      key={index}
      onClick={() => !showFeedback && setSelectedAnswer(option)}
      onMouseEnter={() => !showFeedback && setHoveredAnswer(index)}
      onMouseLeave={() => setHoveredAnswer(null)}
      className={`
        relative w-full p-4 rounded-xl border-2 cursor-pointer
        transition-all duration-300
        ${isHovered && !showFeedback && !isSelected ? '-translate-y-1 shadow-lg' : ''}
        ${isSelected && !showFeedback ? 'scale-[1.02]' : ''}
        ${showFeedback && isThisCorrect ? 'bg-[#83C5BE] border-[#83C5BE]' : ''}
        ${isThisWrong ? 'bg-[#FFF5F2] border-[#E29578]' : ''}
        ${!isSelected && !showFeedback && !isHovered ? 'bg-white border-black' : ''}
        ${showFeedback ? 'cursor-not-allowed' : ''}
      `}
      style={isSelected && !showFeedback ? {
        backgroundColor: '#EBF5F5',
        borderColor: '#006D77',
        boxShadow: '0 0 0 2px rgba(0,109,119,0.2)'
      } : {}}
    >
      {/* PERFECT: Drawing animation from top-left, meeting at bottom-right */}
     {isHovered && !showFeedback && !isSelected && (
  <svg
    className="absolute inset-0 w-full h-full pointer-events-none"
    style={{ overflow: 'visible' }}
  >
    <rect
      x="1"
      y="1"
      width="calc(100% - 2px)"
      height="calc(100% - 2px)"
      rx="12"
      ry="12"
      fill="none"
      stroke="#006D77"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      pathLength="100"
      strokeDasharray="100"
      strokeDashoffset="100"
      className="animate-svg-border"
    />
  </svg>
)}

      {/* Coral dot for wrong answers */}
      {isThisWrong && (
        <div className="absolute top-2 right-2 w-2 h-2 bg-[#E29578] rounded-full animate-pulse" />
      )}

      <div className="relative z-10 flex items-center justify-between">
        <span className={`font-medium text-base sm:text-lg ${
          showFeedback && isThisCorrect ? 'text-white' : 'text-black'
        }`}>
          {option}
        </span>
        {showFeedback && (
          <div>
            {isThisCorrect && (
              <CheckCircle className="w-5 h-5 text-white" />
            )}
            {isThisWrong && (
              <XCircle className="w-5 h-5 text-[#E29578]" />
            )}
          </div>
        )}
      </div>
    </div>
  );
})}
      </div>
    </div>
  );
};

  const renderMatchingPairs = () => {
    const allItemsCount = leftColumnItems.length + rightColumnItems.length;
    const allMatched = matchedPairs.length === allItemsCount && allItemsCount > 0;

    const renderButton = (item) => {
      const isSelected = selectedPairItem?.id === item.id;
      const isMatched = matchedPairs.includes(item.id);
      const isIncorrect = incorrectPair?.item1 === item.text || incorrectPair?.item2 === item.text;
      const isHovered = hoveredAnswer === item.id;

      return (
        <button
          key={item.id}
          onClick={() => handlePairSelection(item)}
          onMouseEnter={() => !isMatched && setHoveredAnswer(item.id)}
          onMouseLeave={() => setHoveredAnswer(null)}
          disabled={isMatched}
          className={`
            relative w-full p-4 h-20 flex items-center justify-center text-center rounded-xl border-2 font-medium
            transition-all duration-300
            ${isHovered && !isMatched ? '-translate-y-1 shadow-lg' : ''}
            ${isMatched ? 'bg-[#83C5BE] border-[#83C5BE] text-white cursor-not-allowed' : ''}
            ${isSelected ? 'bg-gray-100 border-black scale-105' : ''}
            ${isIncorrect ? 'bg-[#FFF5F2] border-[#E29578] animate-shake' : ''}
            ${!isMatched && !isSelected && !isIncorrect ? 'bg-white border-black hover:bg-gray-50' : ''}
          `}
        >
          {/* Animated border on hover */}
          {isHovered && !isMatched && !isSelected && (
            <>
              <div className="absolute top-0 left-0 h-[2px] bg-black animate-border-top" />
              <div className="absolute top-0 right-0 w-[2px] bg-black animate-border-right" />
              <div className="absolute bottom-0 right-0 h-[2px] bg-black animate-border-bottom" />
              <div className="absolute bottom-0 left-0 w-[2px] bg-black animate-border-left" />
            </>
          )}

          {/* Coral dot for incorrect */}
          {isIncorrect && (
            <div className="absolute top-2 right-2 w-2 h-2 bg-[#E29578] rounded-full animate-pulse" />
          )}

          {item.text}
        </button>
      );
    };

    return (
      <div className="space-y-4">
        <div className="bg-white p-4 rounded-xl border-2 border-black relative overflow-hidden">
          <div className="absolute top-2 left-2 w-2 h-2 border-t-2 border-l-2 border-black/20"></div>
          <div className="absolute bottom-2 right-2 w-2 h-2 border-b-2 border-r-2 border-black/20"></div>

          <p className="text-lg font-space font-bold text-black text-center">
            {exercise.question}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-3">
            {leftColumnItems.map(item => renderButton(item))}
          </div>
          <div className="space-y-3">
            {rightColumnItems.map(item => renderButton(item))}
          </div>
        </div>

        {allMatched && !showFeedback && (
          <div className="text-center pt-4">
            <p className="text-black font-semibold">Sehr gut! Alle Paare gefunden.</p>
          </div>
        )}
      </div>
    );
  };

  const renderErrorCorrection = () => {
    const incorrectPart = exercise.incorrectPart;
    const stimulus = exercise.stimulus;
    const incorrectIndex = stimulus.indexOf(incorrectPart);
    const beforeIncorrect = stimulus.substring(0, incorrectIndex);
    const afterIncorrect = stimulus.substring(incorrectIndex + incorrectPart.length);

    const handleCorrection = (newText) => {
      setCorrectedText(newText);
      setIsEditing(false);
    };

    return (
      <div className="space-y-4">
        <div className="bg-white p-4 sm:p-6 rounded-xl border-2 border-black relative overflow-hidden">
          <div className="absolute top-2 left-2 w-2 h-2 border-t-2 border-l-2 border-black/20"></div>
          <div className="absolute bottom-2 right-2 w-2 h-2 border-b-2 border-r-2 border-black/20"></div>

          <p className="text-lg font-space font-bold text-black mb-4">
            {exercise.question}
          </p>

          <div className="text-lg leading-relaxed p-4 bg-gray-50 border-2 border-gray-200 rounded-lg">
            <span className="text-gray-700">{beforeIncorrect}</span>

            {!isEditing ? (
              <span
                className={`mx-1 px-2 py-1 rounded cursor-pointer transition-all ${
                  showFeedback
                    ? isCorrect
                      ? 'bg-[#83C5BE] text-white border-2 border-[#83C5BE]'
                      : 'bg-[#FFF5F2] text-[#C4694F] border-2 border-[#E29578]'
                    : 'bg-[#FFF5F2] border-2 border-[#F0B5A3] text-[#C4694F] hover:bg-[#F0B5A3]'
                }`}
                onClick={() => !showFeedback && setIsEditing(true)}
              >
                {correctedText || incorrectPart}
              </span>
            ) : (
              <input
                type="text"
                className="mx-1 px-2 py-1 border-2 border-black rounded focus:outline-none focus:border-[#3A86FF] touch-manipulation"
                style={{ fontSize: '16px' }}
                defaultValue={incorrectPart}
                autoFocus
                onBlur={(e) => handleCorrection(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleCorrection(e.target.value);
                  }
                }}
                placeholder="Korrigieren Sie hier..."
              />
            )}

            <span className="text-gray-700">{afterIncorrect}</span>
          </div>

          <p className="text-sm text-gray-600 mt-2">
            💡 Klicken Sie auf den <span className="text-[#C4694F] font-medium">markierten</span> Teil, um ihn zu korrigieren.
          </p>
        </div>

        {showFeedback && (
          <div className="p-4 bg-black text-white rounded-xl border-2 border-black">
            <p className="text-sm font-medium mb-2">Korrekte Lösung:</p>
            <p className="text-lg font-bold border-l-4 border-white pl-4">
              "{exercise.correctStimulus}"
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderFillInTheBlanks = () => {
    if (typeof exercise.stimulus !== 'string') {
      return (
        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
          <p className="text-red-800 font-bold">Exercise Configuration Error</p>
          <p className="text-red-700 mt-1">
            The 'stimulus' for this fill-in-the-blanks exercise is not correctly formatted.
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="bg-white p-4 sm:p-6 rounded-xl border-2 border-black relative overflow-hidden">
          <div className="absolute top-2 left-2 w-2 h-2 border-t-2 border-l-2 border-black/20"></div>
          <div className="absolute bottom-2 right-2 w-2 h-2 border-b-2 border-r-2 border-black/20"></div>

          <p className="text-lg font-space font-bold text-black mb-4">
            {exercise.question}
          </p>
          <div className="text-gray-700 text-lg leading-relaxed">
            {exercise.stimulus.split(/(?:__+)|(?:\[---\])/).map((part, index, partsArray) => (
              <span key={index}>
                {part}
                {index < partsArray.length - 1 && (
                  <input
                    type="text"
                    className={`mx-2 px-3 py-2 border-2 rounded-lg text-center font-medium min-w-[120px] touch-manipulation ${
                      showFeedback
                        ? checkWord(fillInAnswers[`blank_${index}`] || '', exercise.answers[index]?.correct || '')
                          ? 'border-[#83C5BE] bg-[#83C5BE] text-white'
                          : 'border-[#E29578] bg-white text-black'
                        : 'border-black focus:border-[#3A86FF] focus:outline-none'
                    }`}
                    style={{ fontSize: '16px' }}
                    disabled={showFeedback}
                    value={fillInAnswers[`blank_${index}`] || ''}
                    onChange={(e) => updateFillInAnswer(`blank_${index}`, e.target.value)}
                    placeholder={showFeedback ? '' : '...'}
                  />
                )}
              </span>
            ))}
          </div>

          {showFeedback && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-sm font-medium text-gray-700 mb-2">Richtige Antworten:</p>
              <div className="space-y-1">
                {exercise.answers.map((answer, index) => (
                  <div key={index} className="text-sm text-gray-600">
                    <span className="font-medium">Lücke {index + 1}:</span>
                    <span className="ml-2 px-2 py-1 bg-black text-white rounded">
                      {answer.correct}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSentenceTransformation = () => (
    <div className="space-y-4">
      <div className="bg-white p-4 sm:p-6 rounded-xl border-2 border-black relative overflow-hidden">
        <div className="absolute top-2 left-2 w-2 h-2 border-t-2 border-l-2 border-black/20"></div>
        <div className="absolute bottom-2 right-2 w-2 h-2 border-b-2 border-r-2 border-black/20"></div>

        <p className="text-lg font-space font-bold text-black mb-4">
          {exercise.question}
        </p>
        <div className="space-y-3">
          <div>
            <p className="text-sm font-medium text-gray-600 mb-1">Ausgangssatz:</p>
            <p className="text-lg text-gray-800 bg-gray-50 p-3 rounded border-2 border-gray-200">
              {exercise.stimulus}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600 mb-1">Ihre Antwort:</p>
            <textarea
              className="w-full p-3 border-2 border-black rounded-lg focus:border-[#3A86FF] focus:outline-none resize-none touch-manipulation"
              style={{ fontSize: '16px' }}
              rows="2"
              disabled={showFeedback}
              value={selectedAnswer}
              onChange={(e) => setSelectedAnswer(e.target.value)}
              placeholder="Schreiben Sie Ihre Antwort hier..."
            />
          </div>
          {showFeedback && (
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Musterlösung:</p>
              <p className="text-lg text-white bg-black p-3 rounded border-2 border-black">
                {exercise.correctAnswer}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderSentenceBuilding = () => {
    return (
      <div className="space-y-6">
        <div className="bg-white p-4 sm:p-6 rounded-xl border-2 border-black relative overflow-hidden">
          <div className="absolute top-2 left-2 w-2 h-2 border-t-2 border-l-2 border-black/20"></div>
          <div className="absolute bottom-2 right-2 w-2 h-2 border-b-2 border-r-2 border-black/20"></div>

          <p className="text-lg font-space font-bold text-black mb-4">
            {exercise.question}
          </p>
          <p className="text-sm text-gray-600 mb-6 font-medium">
            {t('grammar.exercise.sentenceBuilder.tapInstruction')}
          </p>

          {/* Word Bank */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-gray-600">
                {t('grammar.exercise.sentenceBuilder.availableWords')}
              </p>
              <button
                onClick={shuffleWordBank}
                className="flex items-center gap-1 px-3 py-1 text-sm text-black hover:text-white hover:bg-black rounded-lg transition-all border border-black"
                disabled={showFeedback}
              >
                <Shuffle className="w-4 h-4" />
            {t('grammar.exercise.sentenceBuilder.shuffle')}
          </button>
            </div>

            <div className="min-h-[80px] p-4 bg-gray-50 border-2 border-gray-300 rounded-lg">
              {wordBank.length === 0 ? (
                <p className="text-gray-400 text-center py-6 italic font-medium">
                  {t('grammar.exercise.sentenceBuilder.allWordsUsed')}
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {wordBank.map((word) => (
                    <button
                      key={word.id}
                      onClick={() => handleWordBankClick(word)}
                      disabled={showFeedback}
                      className={`px-4 py-3 bg-white border-2 border-black rounded-lg font-medium transition-all ${
                        showFeedback
                          ? 'cursor-not-allowed opacity-60'
                          : 'hover:bg-gray-100 hover:border-[#3A86FF] active:scale-95 cursor-pointer'
                      }`}
                    >
                      {word.text}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sentence Building Area */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-gray-600">Ihr Satz:</p>
              {sentenceArea.length > 0 && (
                <button
                  onClick={clearSentence}
                  className="flex items-center gap-1 px-3 py-1 text-sm text-red-600 hover:text-white hover:bg-red-500 rounded-lg transition-all border border-red-500"
                  disabled={showFeedback}
                >
                  <Trash2 className="w-4 h-4" />
                  Löschen
                </button>
              )}
            </div>

            <div className="min-h-[100px] p-4 bg-white border-2 border-black rounded-lg">
              {sentenceArea.length === 0 ? (
                <p className="text-gray-400 text-center py-8 italic">
                  Tippen Sie auf Wörter oben, um zu beginnen
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {sentenceArea.map((word, index) => (
                    <button
                      key={word.id}
                      onClick={() => handleSentenceClick(word)}
                      disabled={showFeedback}
                      className={`px-4 py-3 bg-black text-white border-2 border-black rounded-lg font-medium transition-all ${
                        showFeedback
                          ? 'cursor-not-allowed opacity-60'
                          : 'hover:bg-gray-800 active:scale-95 cursor-pointer'
                      }`}
                    >
                      {word.text}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Correct Answer */}
          {showFeedback && (
            <div className="mt-4 p-4 bg-black text-white rounded-lg border-2 border-black">
              <p className="text-sm font-medium mb-2">Musterlösung:</p>
              <p className="text-lg font-bold">
                "{exercise.correctAnswer}"
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderExerciseContent = () => {
    switch (exercise.exerciseType) {
      case 'multipleChoice':
        return renderMultipleChoice();
      case 'errorCorrection':
        return renderErrorCorrection();
      case 'fillInTheBlanks':
        return renderFillInTheBlanks();
      case 'matchingPairs':
        return renderMatchingPairs();
      case 'sentenceTransformation':
        return renderSentenceTransformation();
      case 'sentenceBuilding':
        return renderSentenceBuilding();
      default:
        return (
          <div className="text-center py-8">
            <Lightbulb className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">Exercise type "{exercise.exerciseType}" wird bald implementiert.</p>
          </div>
        );
    }
  };

  const isAnswerProvided = () => {
    switch (exercise.exerciseType) {
      case 'multipleChoice':
        return selectedAnswer !== '';
      case 'fillInTheBlanks':
        return Object.keys(fillInAnswers).length > 0;
      case 'matchingPairs':
        const totalItems = leftColumnItems.length + rightColumnItems.length;
        return matchedPairs.length === totalItems && totalItems > 0;
      case 'sentenceTransformation':
        return selectedAnswer.trim() !== '';
      case 'sentenceBuilding':
        return sentenceArea.length > 0;
      case 'errorCorrection':
        return correctedText !== '';
      default:
        return true;
    }
  };

  return (
  <div className="max-w-4xl mx-auto px-0 sm:px-6">
    <div
      className="rounded-lg sm:rounded-2xl border-2 sm:border-[3px] border-black overflow-hidden relative"
      style={{
        backgroundColor: '#FFFFFF',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)'
      }}
    >

        {/* Corner decorations on card */}
        <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-black/20 z-10"></div>
        <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-black/20 z-10"></div>

        {/* Exercise Header - Black & White */}
        <div className="bg-white px-4 sm:px-6 py-4 border-b-2 border-black">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 sm:gap-3 mb-2 flex-wrap">
                {exercise.pillar && (
                  <span className="px-3 py-1 bg-black text-white rounded-full text-xs sm:text-sm font-bold">
                    {exercise.pillar.charAt(0).toUpperCase() + exercise.pillar.slice(1)}
                  </span>
                )}
                {exercise.difficulty && (
                  <span className="px-3 py-1 bg-gray-200 text-black rounded-full text-xs sm:text-sm font-bold">
                    {exercise.difficulty.charAt(0).toUpperCase() + exercise.difficulty.slice(1)}
                  </span>
                )}
                {exercise.estimatedTime && (
                  <span className="px-3 py-1 bg-gray-100 text-black rounded-full text-xs sm:text-sm font-medium border border-black">
                    {exercise.estimatedTime} min
                  </span>
                )}
              </div>
              <h3 className="text-lg sm:text-xl font-space font-black text-black">
                Übung: {exercise.id ? exercise.id.split('-')[1] : 'N/A'}
              </h3>
            </div>
            <button
              onClick={onToggleBookmark}
              title={isBookmarked ? t('grammar.exercise.removeBookmark') : t('grammar.exercise.bookmark')}
              className={`p-2 rounded-full transition-all hover:scale-110 ${
                isBookmarked
                  ? 'bg-black text-white'
                  : 'bg-gray-100 text-black hover:bg-gray-200'
              }`}
            >
              <Bookmark className={`w-5 h-5 ${isBookmarked ? 'fill-current' : ''}`} />
            </button>
          </div>
        </div>

        {/* Exercise Content */}
        <div className="p-4 sm:p-6">
          {renderExerciseContent()}

          {/* Feedback Section - Black & White */}
          {showFeedback && (
            <div className={`mt-6 p-4 rounded-xl border-2 relative overflow-hidden ${
              isCorrect
                ? 'bg-[#83C5BE] border-[#83C5BE]'
                : 'bg-[#FFF5F2] border-[#E29578]'
            }`}>
              {/* Coral dot for incorrect */}
              {!isCorrect && (
                <div className="absolute top-2 right-2 w-3 h-3 bg-[#E29578] rounded-full animate-pulse" />
              )}

              <div className="flex items-start gap-3">
                {isCorrect ? (
                  <CheckCircle className="w-6 h-6 text-white mt-0.5 flex-shrink-0" />
                ) : (
                  <XCircle className="w-6 h-6 text-[#E29578] mt-0.5 flex-shrink-0" />
                )}
                <div className="flex-1">
                  <p className={`font-space font-bold text-lg mb-2 ${
                    isCorrect ? 'text-white' : 'text-[#C4694F]'
                  }`}>
                    {isCorrect ? '✓ Richtig!' : '✗ Nicht ganz richtig'}
                  </p>
                  <p className={`text-sm leading-relaxed ${
                    isCorrect ? 'text-white/90' : 'text-gray-700'
                  }`}>
                    {exercise.feedback}
                  </p>
                  {exercise.grammarReference && (
                    <p className={`text-xs mt-2 ${
                      isCorrect ? 'text-white/70' : 'text-gray-500'
                    }`}>
                      Weitere Info: {exercise.grammarReference}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons with Wave Effect */}
          <div className="mt-6 flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 pt-4 border-t-2 border-gray-200">
            {/* Retry Button with Wave Effect */}
            <button
              onClick={handleReset}
              onMouseEnter={() => setIsRetryHovered(true)}
              onMouseLeave={() => setIsRetryHovered(false)}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 sm:py-3 min-h-[44px] bg-white rounded-lg border-2 border-black relative overflow-hidden transition-all duration-300 hover:shadow-lg order-2 sm:order-1 touch-manipulation"
            >
              {/* Wave fill effect */}
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: 0,
                  width: isRetryHovered ? '100%' : '0%',
                  background: 'linear-gradient(to right, #d1d5db, #9ca3af, #6b7280)',
                  transition: 'width 0.7s ease-out',
                  zIndex: 0
                }}
              />
              <RotateCcw
                className="w-4 h-4 relative z-10 transition-colors duration-500"
                style={{ color: isRetryHovered ? '#ffffff' : '#000000' }}
              />
              <span
                className="font-medium relative z-10 transition-colors duration-500"
                style={{ color: isRetryHovered ? '#ffffff' : '#000000' }}
              >
                Wiederholen
              </span>
            </button>

            {/* Check Answer / Next Button with Wave Effect */}
            {!showFeedback ? (
              <button
                onClick={handleSubmit}
                onMouseEnter={() => setIsCheckHovered(true)}
                onMouseLeave={() => setIsCheckHovered(false)}
                disabled={!isAnswerProvided()}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 min-h-[44px] bg-white rounded-lg border-2 border-black relative overflow-hidden transition-all duration-300 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed order-1 sm:order-2 touch-manipulation"
              >
                {/* Wave fill effect */}
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: 0,
                    width: isCheckHovered && isAnswerProvided() ? '100%' : '0%',
                    background: 'linear-gradient(to right, #9ca3af, #4b5563, #000000)',
                    transition: 'width 0.7s ease-out',
                    zIndex: 0
                  }}
                />
                <span
                  className="font-space font-bold relative z-10 transition-colors duration-500"
                  style={{ color: isCheckHovered && isAnswerProvided() ? '#ffffff' : '#000000' }}
                >
                  Antwort prüfen
                </span>
              </button>
            ) : (
              <button
                onClick={handleNext}
                onMouseEnter={() => setIsNextHovered(true)}
                onMouseLeave={() => setIsNextHovered(false)}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 min-h-[44px] bg-white rounded-lg border-2 border-black relative overflow-hidden transition-all duration-300 hover:shadow-xl order-1 sm:order-2 touch-manipulation"
              >
                {/* Wave fill effect */}
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: 0,
                    width: isNextHovered ? '100%' : '0%',
                    background: 'linear-gradient(to right, #9ca3af, #4b5563, #000000)',
                    transition: 'width 0.7s ease-out',
                    zIndex: 0
                  }}
                />
                <span
                  className="font-space font-bold relative z-10 transition-colors duration-500"
                  style={{ color: isNextHovered ? '#ffffff' : '#000000' }}
                >
                  Weiter
                </span>
                <ArrowRight
                  className="w-4 h-4 relative z-10 transition-colors duration-500"
                  style={{ color: isNextHovered ? '#ffffff' : '#000000' }}
                />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GrammarExercise;